"use client";
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useContext,
} from "react";
import styles from "./header-opt.module.scss";
import Image from "next/image";
import Link from "next/link";
import SearchIcon from "@/SvgIcons/SearchIcon";
import Close from "@/SvgIcons/Close";
import ClientSideButton from "@/components/molecules/Carousels/HeroCarousel/ClientSideButton";
import { IModalActions, ModalTypes } from "@/types/modal";
import Hamburger from "@/SvgIcons/Hamburger";
import { usePathname } from "next/navigation";
import MobileMenuOpt from "./MobileMenuOpt";
import DMenuItems from "./Desktop/DMenuItems";
import ProgramSearcher from "./ProgramSearcher";
import DProgramSearcher from "./Desktop/DProgramSearcher";
import DesktopProgramMenu from "./Desktop/DesktopProgramMenu";
import popupData from "@/data/popup-data.json";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import { GA_EVENTS, GAEvent } from "@/helpers/gaEvents";
import ProgramSearchResults from "./ProgramSearchResults";
import { ModalContext } from "@/context/ModalContext";
import LoginUserIcon from "@/SvgIcons/LoginUserIcon";
import { LoginIcon } from "@/SvgIcons/Login";
import { useSearchDebounce } from "@/helpers/useSearchDebounce";
import Script from "next/script";

export interface AssociatedProgramInterface {
  attributes: {
    BasicDetails: {
      Heading?: string;
      SpecializationText?: string | null;
      enabled?: boolean;
      url_key?: string;
      ProgramType?: string;
      CardActionBGColor?: string;
      Title1?: string;
      Title2?: string;
      PROGRAM_ID?: string;
      Custom10PgType?: string | null;
    };
  };
}
interface HeaderOptProps {
  isSponsoredCertificates?: boolean;
  associatedPrograms: AssociatedProgramInterface[];
}

const HeaderOpt: React.FC<HeaderOptProps> = ({
  isSponsoredCertificates = false,
  associatedPrograms,
}) => {
  const { LEAD_INITIATE } = GAEvent;
  const [menuPage, setMenuPage] = useState<number>(0);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchValue = useSearchDebounce(searchValue, 300);
  const pathname = usePathname();
  const [isStickyHeader, setIsStickyHeader] = useState<boolean>(false);
  const lastScrollY = useRef<number>(0);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [isDelayingEngatiScript, setIsDelayingEngatiScript] = useState(false)
  const headerRef = useRef(null);
  const isCertificationsPage =
    pathname == "/certifications/thank-you" ||
    pathname == "/certifications/payment-failed" ||
    pathname == "/certifications";
  const { modalDispatch } = useContext(ModalContext);

  useEffect(() => {
    pushToDataLayer({
      event: "page_type",
      page_type: getGlobalStateValue("pageType"),
    });
  }, [pathname]);

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setHeaderHeight(entry.contentRect.height + 2 * entry.contentRect.top);
      }
    });

    observer.observe(headerElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if(currentScrollY>= 15 && !isSponsoredCertificates) {  
        setIsDelayingEngatiScript(true)
      }
      if (currentScrollY < lastScrollY.current && currentScrollY > 108) {
        setIsStickyHeader(true);
      } else {
        setIsStickyHeader(false);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      const engatiChat = document.getElementById("engtLauncherIcon");
      if (engatiChat) {
        engatiChat.style.display = "none";
      }
    } else {
      const engatiChat = document.getElementById("engtLauncherIcon");
      if (engatiChat) {
        engatiChat.style.display = "table";
      }
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen || searchValue) {
      document.body.classList.add("scroll-lock");
    } else {
      document.body.classList.remove("scroll-lock");
    }

    return () => {
      document.body.classList.remove("scroll-lock");
    };
  }, [isMenuOpen, searchValue]);

  useEffect(() => {
    setIsMenuOpen(false);
    setMenuPage(0);
  }, [pathname]);

  const toggleMenu = useCallback((): void => {
    setSearchValue("");
    setIsMenuOpen((prev) => !prev);
  }, []);

  const GAEventsHandler = () => {
    pushToDataLayer({
      event: "logo_click",
      section_name: "top navigation bar",
      page_type: getGlobalStateValue("pageType"),
    });
  };
  const onLoginHandler = () => {
    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.LoginModal,
        modalData: { associatedPrograms, isLandingPage: true },
      },
    });

    setTimeout(() => {
      pushToDataLayer({
        event: "student_login_initiate",
        cta_text: "STUDENT LOGIN",
        page_type: getGlobalStateValue("pageType"),
        page_url: location.href,
      });
    }, 2500);
  };
  return (
    <>
    {isDelayingEngatiScript && (
        <Script
          id="engati-script"
          async
          dangerouslySetInnerHTML={{
            __html: `!function(e,t,a){var c=e.head||e.getElementsByTagName("head")[0],n=e.createElement("script");n.defer=!0, n.type="text/javascript",n.src=t+"/static/js/widget.js?config="+JSON.stringify(a),c.appendChild(n)}(document,"https://app.engati.com",{bot_key:"bf113f277dd14593",welcome_msg:true,branding_key:"default",server:"https://app.engati.com",e:"p" });`,
          }}
          strategy="lazyOnload"
        />
      )}
      <header
        className={`${styles.header} ${isMenuOpen ? styles.open : ""} ${
          isStickyHeader ? styles.sticky : ""
        }`}
        ref={headerRef}
      >
        <div className={styles.headerWrapper}>
          <Link
            href="/"
            target="_self"
            className={styles.headerLogo}
            onClick={GAEventsHandler}
          >
            <Image
              src="/_a/images/amity_logo_c.svg"
              alt="Amity Logo"
              width={124}
              height={44}
              title="Amity Logo"
              priority
              sizes="(max-width: 349px) 86px, 124px"
              decoding="async"
            />
          </Link>
          <div className={styles.headerBtnWrapper}>
            {pathname != "/predegree" &&
              !isCertificationsPage &&
              !isSponsoredCertificates && (
                <ClientSideButton
                  modalType={ModalTypes.ConnectModal}
                  GADataNew={{
                    ...GA_EVENTS.get(LEAD_INITIATE),
                    cta_text: "Enquire Now",
                    section_name: "Header",
                    header_title: "NA",
                    isPageType: true,
                    page_url: true,
                  }}
                  webEngageEvents={{
                    "Section Name": "Header",
                    isPageType: true,
                    eventName: "Enquire Now Clicked"
                  }}
                  modalData={{
                    associatedPrograms,
                    isLandingPage: true,
                    popup: popupData,
                  }}
                  styles={{ backgroundColor: "white" }}
                  className={styles.enquireNowBtn}
                  ctaText={"ENQUIRE NOW"}
                  enquiryNow={true}
                />
              )}
            {pathname != "/predegree" &&
              !isCertificationsPage &&
              !isSponsoredCertificates && (
                <ClientSideButton
                  modalType={
                    isSponsoredCertificates
                      ? ModalTypes.ConnectModalFormSponsoredCourses
                      : ModalTypes.ConnectModal
                  }
                  GADataNew={{
                    ...GA_EVENTS.get(LEAD_INITIATE),
                    cta_text: "Apply Now",
                    section_name: "Header",
                    header_title: "NA",
                    isPageType: true,
                    page_url: true,
                  }}
                  webEngageEvents={{
                    "Section Name": "Header",
                    isPageType: true,
                    eventName: "Apply Now Clicked"
                  }}
                  modalData={{
                    associatedPrograms,
                    isLandingPage: true,
                    popup: popupData,
                  }}
                  ctaText={isSponsoredCertificates ? "ENROLL NOW" : "APPLY NOW"}
                  className={styles.applyNowBtn}
                />
              )}
            {pathname == "/predegree" && (
              <a
                href="https://predegrees.amityonline.com/login/index.php"
                className={
                  "flex items-center border-[1.5px] border-[#1a325d] px-3 py-2 text-[#1a325d] bg-[#ffc907] rounded-[50px]"
                }
              >
                <span className="flex items-center">
                  <LoginIcon />
                  <span className="text-[0.8rem] uppercase font-bold">
                    Login
                  </span>
                </span>
              </a>
            )}
            {(pathname == "/predegree" ||
              isCertificationsPage ||
              isSponsoredCertificates) && (
              <div className={styles.searchWrapper} onClick={toggleMenu}>
                <SearchIcon className={styles.searchIcon} color="#fff" />
              </div>
            )}

            <button
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              className={styles.menuToggleBtn}
            >
              {isMenuOpen ? <Close /> : <Hamburger />}
            </button>
          </div>

          {/* Desktop Header */}

          <div className={styles.desktopHeaderBtnWrapper}>
            <DMenuItems setIsMenuItem={setIsMenuOpen} isMenuOpen={isMenuOpen} />
            <DProgramSearcher setIsMenuOpen={setIsMenuOpen} />
            {pathname === "/predegree" && (
              <a href="https://predegrees.amityonline.com/login/index.php">
                <button className={styles.loginBtnWrapper}>
                  <LoginIcon />
                  LOGIN
                </button>
              </a>
            )}
            {pathname !== "/predegree" && !isSponsoredCertificates && (
              <button className={styles.dStudentLogin} onClick={onLoginHandler}>
                <LoginUserIcon
                  className={styles.svgHover}
                  height={"0.938rem"}
                  width={"0.938rem"}
                />
                EXISTING STUDENT LOGIN
              </button>
            )}
            <div className={styles.applyBtnWrapper}>
              {pathname != "/predegree" &&
                !isCertificationsPage &&
                !isSponsoredCertificates && (
                  <ClientSideButton
                    modalType={ModalTypes.ConnectModal}
                    GADataNew={{
                      ...GA_EVENTS.get(LEAD_INITIATE),
                      cta_text: "Enquire Now",
                      section_name: "Header",
                      header_title: "NA",
                      isPageType: true,
                      page_url: true,
                    }}
                    webEngageEvents={{
                      "Section Name": "Header",
                      isPageType: true,
                    }}
                    modalData={{
                      associatedPrograms,
                      isLandingPage: true,
                      popup: popupData,
                    }}
                    styles={{ backgroundColor: "white" }}
                    className={styles.enquireNowBtn}
                    ctaText={"ENQUIRE NOW"}
                    enquiryNow={true}
                  />
                )}
              {pathname != "/predegree" && !isCertificationsPage && (
                <ClientSideButton
                  modalType={
                    isSponsoredCertificates
                      ? ModalTypes.ConnectModalFormSponsoredCourses
                      : ModalTypes.ConnectModal
                  }
                  GADataNew={{
                    ...GA_EVENTS.get(LEAD_INITIATE),
                    cta_text: "Apply Now",
                    section_name: "Header",
                    header_title: "NA",
                    isPageType: true,
                    page_url: true,
                  }}
                  webEngageEvents={{
                    "Section Name": "Header",
                    isPageType: true,
                  }}
                  modalData={{
                    associatedPrograms,
                    isLandingPage: true,
                    popup: popupData,
                  }}
                  ctaText={isSponsoredCertificates ? "ENROLL NOW" : "APPLY NOW"}
                  className={styles.applyNowBtn}
                />
              )}
            </div>
          </div>
        </div>

        <MobileMenuOpt
          isSponsoredCertificates={isSponsoredCertificates}
          isMenuOpen={isMenuOpen}
          menuPage={menuPage}
          setMenuPage={setMenuPage}
          associatedPrograms={associatedPrograms}
          setIsMenuOpen={setIsMenuOpen}
        />
        {isMenuOpen && (
          <DesktopProgramMenu
            headerHeight={headerHeight}
            setIsMenuOpen={setIsMenuOpen}
          />
        )}
      </header>
      {!isMenuOpen && !(isCertificationsPage || isSponsoredCertificates) && (
        <div className={styles.homeSearchWrapper}>
          <ProgramSearcher
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
          {searchValue && (
            <div className={styles.homeSearchResults}>
              <ProgramSearchResults searchValue={debouncedSearchValue} actualSearchVal={searchValue}/>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default React.memo(HeaderOpt);
